<script setup lang="ts">
    import { __ } from '@/js/stores/expressions';
    import UsersTableCell from './UsersTableCell.vue';
    import TablePaginator from '../dataTable/TablePaginator.vue';
    import TableLoadingOverlay from '../dataTable/TableLoadingOverlay.vue';
    import { UsersTableColumn, UsersTableItem, useUsersTableData } from '@/js/stores/users-table';
    import { Column, DataTable, PageState } from 'primevue';
    import { useNumericQueryParam, useQueryString } from '@/js/composables/useQueryString';
    import { useTablePageSize } from '@/js/composables/useTablePageSize';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import { onMounted, ref } from 'vue';
    import { deleteUser } from '@/js/services/api/user/userApi';
    import SortableColumnHeader from '../dataTable/SortableColumnHeader.vue';
    import { TrashIcon } from '@lucide/vue';
    import BottomScrollFade from '../dataTable/BottomScrollFade.vue';

    const usersTable = useUsersTableData();

    const columns: UsersTableColumn[] = [
        { field: 'name', name: __('users-table-column-name'), sortable: true },
        { field: 'role', name: __('users-table-column-role'), sortable: true },
        { field: 'lastSeen', name: __('users-table-column-last-seen'), sortable: true },
        { field: 'hasOverride', name: __('users-table-column-overrides'), sortable: true },
        { field: 'actions', name: '', sortable: false },
    ];

    const page = useNumericQueryParam('page', {
        startIndex: 0,
        oneBased: true,
        updateMode: 'push',
    });
    const pageSize = useTablePageSize('users');

    const querystring = useQueryString();
    const paginationChange = async (e: PageState) => {
        const newPage = e.rows == pageSize.value ? e.page + 1 : 1;

        await querystring.setMany({ page: newPage, rows: e.rows }, 'push');
    };

    const confirm = useConfirmationDialog();

    const confirmDeleteUser = async (user: UsersTableItem) => {
        const confirmed = await confirm.openDialog({
            title: __('confirm-delete-user-title'),
            message: __('confirm-delete-user-message', {
                name: user.name,
            }),
            confirmText: __('yes-confirm-delete'),
            cancelText: __('no'),
            confirmIcon: TrashIcon,
        });

        if (confirmed) {
            await deleteUser(user.id);

            await usersTable.fetchUsers();
        }
    };

    const columnWidths: Record<string, string> = {
        name: '35%',
        role: '20%',
        lastSeen: '20%',
        hasOverride: '20%',
        actions: '5%',
    };

    onMounted(() => {
        usersTable.fetchUsers();
    });

    const tableRef = ref<HTMLElement | null>(null);
</script>

<template>
    <div ref="tableRef" class="echo-table-wrapper echo-table-wrapper--fill">
        <DataTable
            class="p-datatable-no-top-border row-padding"
            :value="usersTable.tableData"
            :loading="usersTable.loading"
            data-key="id"
            paginator
            :rows="pageSize"
            :total-records="usersTable.tableData.length"
            :page="page"
            :first="page * pageSize"
            :scrollable="true"
            scroll-height="flex"
        >
            <template #loadingicon>
                <TableLoadingOverlay />
            </template>
            <Column
                v-for="col in columns"
                :key="col.field"
                :field="col.field"
                :style="{ width: columnWidths[col.field] ?? 'auto' }"
            >
                <template #header>
                    <SortableColumnHeader
                        :name="col.name"
                        :sortable="col.sortable"
                        :active-sort="
                            usersTable.sortBy === col.field ? usersTable.sortDirection : undefined
                        "
                        @sort="(dir) => col.sortable && usersTable.setSort(col.field, dir)"
                    />
                </template>
                <template #body="{ data }">
                    <UsersTableCell :data="data" :column="col.field" @delete="confirmDeleteUser" />
                </template>
            </Column>
            <template #paginatorcontainer="slotProps">
                <TablePaginator
                    :first="slotProps.first"
                    :rows="slotProps.rows"
                    :page="slotProps.page"
                    :total-records="slotProps.totalRecords ?? 0"
                    :page-count="slotProps.pageCount ?? 0"
                    @pagination-change="paginationChange"
                />
            </template>
        </DataTable>
        <BottomScrollFade :container-ref="tableRef" />
    </div>
</template>
